import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService } from '../services/authService';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'epprem_theme_preference';

interface UserThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  initTheme: (userId: string, themePreference: Theme) => void;
  clearTheme: () => void;
}

const UserThemeContext = createContext<UserThemeContextType | undefined>(undefined);

function applyThemeClass(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Read the cached theme preference from localStorage (fast, synchronous)
function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch { /* noop */ }
  return 'light';
}

function persistThemeLocally(theme: Theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch { /* noop */ }
}

interface UserThemeProviderProps {
  children: ReactNode;
}

export function UserThemeProvider({ children }: UserThemeProviderProps) {
  // Initialize from localStorage immediately to prevent FOUC
  const [theme, setTheme] = useState<Theme>(() => {
    const cached = getStoredTheme();
    applyThemeClass(cached);
    return cached;
  });
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // On mount: also check the stored user profile (may have newer value from server)
  useEffect(() => {
    const storedUser = authService.getCurrentUser();
    if (storedUser) {
      const savedTheme = (storedUser.themePreference as Theme) || 'light';
      setTheme(savedTheme);
      setCurrentUserId(storedUser.userId);
      applyThemeClass(savedTheme);
      persistThemeLocally(savedTheme);
    }
  }, []);

  // Called by AuthContext after successful login
  const initTheme = useCallback((userId: string, themePreference: Theme) => {
    setCurrentUserId(userId);
    setTheme(themePreference);
    applyThemeClass(themePreference);
    persistThemeLocally(themePreference);
  }, []);

  // Called by AuthContext after logout
  const clearTheme = useCallback(() => {
    setCurrentUserId(null);
    setTheme('light');
    applyThemeClass('light');
    persistThemeLocally('light');
  }, []);

  const toggleTheme = useCallback(async () => {
    const newTheme: Theme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    applyThemeClass(newTheme);
    persistThemeLocally(newTheme);

    // Persist to database if user is authenticated
    if (currentUserId) {
      try {
        await authService.updateThemePreference(currentUserId, newTheme);
      } catch (err) {
        console.error('Failed to persist theme preference:', err);
        // Revert on failure
        setTheme(theme);
        applyThemeClass(theme);
      }
    }
  }, [theme, currentUserId]);

  return (
    <UserThemeContext.Provider value={{ theme, toggleTheme, initTheme, clearTheme }}>
      {children}
    </UserThemeContext.Provider>
  );
}

export function useUserTheme() {
  const context = useContext(UserThemeContext);
  if (context === undefined) {
    throw new Error('useUserTheme must be used within a UserThemeProvider');
  }
  return context;
}
